import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { getDvSourceImageUrl } from "../../../functions/themeAssets";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MAX_TITLE_LEN = 200;
const MAX_THANKYOU_TEXT_LEN = 300;
/** Per-right-answer points and wrong-answer penalty (admin rules page). */
const MIN_SCORE_MARKS = 1;
const MAX_SCORE_MARKS = 100;
const MIN_LIFELINE_COUNT = 1;
const MAX_LIFELINE_COUNT = 20;
const MAX_TITLE_FONT_PX = 200;
const MIN_TITLE_FONT_PX = 8;
const DEFAULT_TITLE_FONT_PX = 22;

const BEGIN_IMAGE_FIELDS = [
  {
    key: "begin_desk",
    label: "Begin desktop background",
    dimensions: [1920, 1080],
    hint: "Required size: 1920×1080 px. JPG or PNG, max 5MB.",
  },
  {
    key: "begin_mob",
    label: "Begin mobile background",
    dimensions: [414, 896],
    hint: "Required size: 414×896 px. JPG or PNG, max 5MB.",
  },
];

const VALID_BEGIN_EXT = ["jpg", "jpeg", "png"];
const MAX_BEGIN_SIZE_MB = 5;

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to read image"));
    };
    img.src = url;
  });

const isThemeFlagOn = (val, defaultOn = false) => {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
};

/** Digits only; empty while typing; max capped (min applied on blur). */
const normalizeTitleFontPxInput = (raw, max) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n)) return "";
  if (n > max) return String(max);
  return String(n);
};

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

/** Digits only; no negatives, no letters. Empty while typing; 0 and below become "". Capped at max. */
const normalizeMarksInput = (raw, max) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1) return "";
  if (n > max) return String(max);
  return String(n);
};

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [landingTitle, setLandingTitle] = useState("");
  const [thankyouText, setThankyouText] = useState("");
  const [thankyouBadgeText, setThankyouBadgeText] = useState("");
  const [points, setPoints] = useState("1");
  const [obstaclePoints, setObstaclePoints] = useState("1");
  const [questionOnObstacle, setQuestionOnObstacle] = useState("2");
  const [wrongPoints, setWrongPoints] = useState("1");
  const [endlessRunner, setEndlessRunner] = useState(true);
  const [quizRunner, setQuizRunner] = useState(true);
  const [endlessEasy, setEndlessEasy] = useState(true);
  const [endlessMedium, setEndlessMedium] = useState(true);
  const [endlessHard, setEndlessHard] = useState(true);
  const [endlessLifeline, setEndlessLifeline] = useState(false);
  const [endlessLifelineCount, setEndlessLifelineCount] = useState("3");
  const [titleDesktopFontPx, setTitleDesktopFontPx] = useState(String(DEFAULT_TITLE_FONT_PX));
  const [titleColor, setTitleColor] = useState("#000000");
  const [gameTextColor, setGameTextColor] = useState("#111111");
  const [beltColor, setBeltColor] = useState("#c4a574");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [buttonColor2, setButtonColor2] = useState("#d45a7a");
  const [buttonTextColor, setButtonTextColor] = useState("#000000");

  const [beginFiles, setBeginFiles] = useState({ begin_desk: null, begin_mob: null });
  const [beginPreviews, setBeginPreviews] = useState({ begin_desk: null, begin_mob: null });
  const beginFileInputs = useRef({});
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    setLandingTitle(String(data.landing_page_title ?? "").replace(/^\s+/, ""));
    setThankyouText(String(data.custom_text_thank_you_page ?? "").trim());
    setThankyouBadgeText(String(data.custom_text_thank_you_page_badge_earned ?? "").trim());

    setPoints(String(clampInt(data.points, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS)));
    setObstaclePoints(
      String(clampInt(data.obstacle_points, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS))
    );
    setQuestionOnObstacle(
      String(clampInt(data.question_on_obstacle, MIN_SCORE_MARKS, MAX_SCORE_MARKS, 2))
    );
    setWrongPoints(String(clampInt(data.wrong_points, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS)));
    setEndlessRunner(isThemeFlagOn(data.endless_runner));
    setQuizRunner(isThemeFlagOn(data.quiz_runner));
    setEndlessEasy(isThemeFlagOn(data.endless_easy, true));
    setEndlessMedium(isThemeFlagOn(data.endless_medium, true));
    setEndlessHard(isThemeFlagOn(data.endless_hard, true));
    setEndlessLifeline(isThemeFlagOn(data.endless_lifeline));
    setEndlessLifelineCount(
      String(clampInt(data.endless_lifeline_count, MIN_LIFELINE_COUNT, MAX_LIFELINE_COUNT, 3))
    );

    const tf = data.landing_page_title_desktop_font_size_px;
    if (tf == null || tf === "") {
      setTitleDesktopFontPx(String(DEFAULT_TITLE_FONT_PX));
    } else {
      setTitleDesktopFontPx(String(clampInt(tf, MIN_TITLE_FONT_PX, MAX_TITLE_FONT_PX, DEFAULT_TITLE_FONT_PX)));
    }

    setTitleColor(toColorInputValue(data.landing_page_title_color));
    setGameTextColor(toColorInputValue(data.game_text_color || "#111111"));
    setBeltColor(toColorInputValue(data.belt_color || "#c4a574"));
    setButtonColor(toColorInputValue(data.button_color));
    setButtonColor2(toColorInputValue(data.button_color_2 || "#d45a7a"));
    setButtonTextColor(toColorInputValue(data.button_Textcolor ?? data.button_text_color));

    setBeginFiles({ begin_desk: null, begin_mob: null });
    setBeginPreviews({
      begin_desk: data.begin_desk ? getDvSourceImageUrl(data.begin_desk) : null,
      begin_mob: data.begin_mob ? getDvSourceImageUrl(data.begin_mob) : null,
    });
  }, [data]);

  const validateBeginImage = async (file, dimensions) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!VALID_BEGIN_EXT.includes(ext)) {
      return `Only ${VALID_BEGIN_EXT.join(", ").toUpperCase()} formats allowed`;
    }
    if (file.size > MAX_BEGIN_SIZE_MB * 1024 * 1024) {
      return `File size exceeds ${MAX_BEGIN_SIZE_MB}MB limit`;
    }
    try {
      const { width, height } = await getImageDimensions(file);
      const [reqW, reqH] = dimensions;
      if (width !== reqW || height !== reqH) {
        return `Image must be exactly ${reqW}×${reqH} px (selected: ${width}×${height} px)`;
      }
    } catch {
      return "Failed to validate image dimensions";
    }
    return null;
  };

  const handleBeginFileChange = async (field, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const error = await validateBeginImage(file, field.dimensions);
    if (error) {
      await Swal.fire({ title: "Invalid Image", text: error, icon: "error" });
      setBeginFiles((prev) => ({ ...prev, [field.key]: null }));
      return;
    }

    setBeginFiles((prev) => ({ ...prev, [field.key]: file }));
    setBeginPreviews((prev) => ({
      ...prev,
      [field.key]: URL.createObjectURL(file),
    }));
  };

  const setEndlessDifficulty = (key, on) => {
    if (!on) {
      const next = {
        easy: endlessEasy,
        medium: endlessMedium,
        hard: endlessHard,
        [key]: false,
      };
      if (!next.easy && !next.medium && !next.hard) return;
    }
    if (key === "easy") setEndlessEasy(on);
    else if (key === "medium") setEndlessMedium(on);
    else setEndlessHard(on);
  };

  const validate = () => {
    const title = landingTitle.trim();
    if (!title) return { ok: false, msg: "Title cannot be empty." };
    if (title.length > MAX_TITLE_LEN) return { ok: false, msg: `Title must be at most ${MAX_TITLE_LEN} characters.` };

    const ty = thankyouText.trim();
    if (ty.length > MAX_THANKYOU_TEXT_LEN) {
      return { ok: false, msg: `Thank you text must be at most ${MAX_THANKYOU_TEXT_LEN} characters.` };
    }
    const tyBadge = thankyouBadgeText.trim();
    if (tyBadge.length > MAX_THANKYOU_TEXT_LEN) {
      return {
        ok: false,
        msg: `Thank you message (badge earned) must be at most ${MAX_THANKYOU_TEXT_LEN} characters.`,
      };
    }

    if (!endlessRunner && !quizRunner) {
      return { ok: false, msg: "Turn on Endless runner or Quiz runner." };
    }
    if (endlessRunner && !endlessEasy && !endlessMedium && !endlessHard) {
      return { ok: false, msg: "Turn on at least one difficulty (Easy, Medium, or Hard)." };
    }

    return {
      ok: true,
      payload: {
        landing_page_title: title,
        custom_text_thank_you_page: ty,
        custom_text_thank_you_page_badge_earned: tyBadge,
        points: clampInt(points, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS),
        obstacle_points: clampInt(obstaclePoints, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS),
        question_on_obstacle: clampInt(
          questionOnObstacle,
          MIN_SCORE_MARKS,
          MAX_SCORE_MARKS,
          2
        ),
        wrong_points: clampInt(wrongPoints, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS),
        endless_runner: endlessRunner ? "true" : "false",
        quiz_runner: quizRunner ? "true" : "false",
        endless_easy: endlessEasy ? "true" : "false",
        endless_medium: endlessMedium ? "true" : "false",
        endless_hard: endlessHard ? "true" : "false",
        endless_lifeline: endlessRunner && endlessLifeline ? "true" : "false",
        endless_lifeline_count: clampInt(
          endlessLifelineCount,
          MIN_LIFELINE_COUNT,
          MAX_LIFELINE_COUNT,
          3
        ),
        landing_page_title_desktop_font_size_px: clampInt(
          titleDesktopFontPx === "" ? DEFAULT_TITLE_FONT_PX : titleDesktopFontPx,
          MIN_TITLE_FONT_PX,
          MAX_TITLE_FONT_PX,
          DEFAULT_TITLE_FONT_PX
        ),
        landing_page_title_color: titleColor,
        game_text_color: gameTextColor,
        belt_color: beltColor,
        button_color: buttonColor,
        button_color_2: buttonColor2,
        button_Textcolor: buttonTextColor,
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    const hasBeginFiles = BEGIN_IMAGE_FIELDS.some(({ key }) => beginFiles[key]);

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      if (hasBeginFiles) {
        const formData = new FormData();
        formData.append("themeName", currentTheme);
        BEGIN_IMAGE_FIELDS.forEach(({ key }) => {
          if (beginFiles[key]) formData.append(key, beginFiles[key]);
        });
        await uploadThemeMedia({ formData, token: adminToken });
      }

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire("Success", "Data updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout rules-php-layout--two">
        <div className="themeupdate-col rules-php-col">
          <div
            className="themeupdate-card rules-php-card"
            data-tour="tour-theme-settings"
          >
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Theme settings</h4>

              <div className="rules-tour-modes" data-tour="tour-game-modes">
                <div className="rules-php-marks-row rules-php-toggle-row">
                  <label htmlFor="rules-endless-runner" className="rules-php-marks-label">
                    Endless runner
                  </label>
                  <Switch
                    id="rules-endless-runner"
                    checked={endlessRunner}
                    onChange={(on) => {
                      setEndlessRunner(on);
                      if (on && !endlessEasy && !endlessMedium && !endlessHard) {
                        setEndlessEasy(true);
                        setEndlessMedium(true);
                        setEndlessHard(true);
                      }
                    }}
                    onColor={buttonColor}
                    offColor="#cbd5e1"
                    onHandleColor="#ffffff"
                    offHandleColor="#ffffff"
                    height={22}
                    width={48}
                    handleDiameter={18}
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>

                {endlessRunner ? (
                  <div className="rules-php-nested">
                    <p className="rules-php-nested-caption">Difficulty levels</p>
                    {[
                      { id: "easy", label: "Easy", checked: endlessEasy },
                      { id: "medium", label: "Medium", checked: endlessMedium },
                      { id: "hard", label: "Hard", checked: endlessHard },
                    ].map((d) => (
                      <div
                        key={d.id}
                        className="rules-php-marks-row rules-php-toggle-row"
                      >
                        <label
                          htmlFor={`rules-endless-${d.id}`}
                          className="rules-php-marks-label"
                        >
                          {d.label}
                        </label>
                        <Switch
                          id={`rules-endless-${d.id}`}
                          checked={d.checked}
                          onChange={(on) => setEndlessDifficulty(d.id, on)}
                          onColor={buttonColor}
                          offColor="#cbd5e1"
                          onHandleColor="#ffffff"
                          offHandleColor="#ffffff"
                          height={22}
                          width={48}
                          handleDiameter={18}
                          uncheckedIcon={false}
                          checkedIcon={false}
                        />
                      </div>
                    ))}

                    <div className="rules-php-marks-row rules-php-toggle-row">
                      <label htmlFor="rules-endless-lifeline" className="rules-php-marks-label">
                        Limited plays
                      </label>
                      <Switch
                        id="rules-endless-lifeline"
                        checked={endlessLifeline}
                        onChange={setEndlessLifeline}
                        onColor={buttonColor}
                        offColor="#cbd5e1"
                        onHandleColor="#ffffff"
                        offHandleColor="#ffffff"
                        height={22}
                        width={48}
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                      />
                    </div>

                    {endlessLifeline ? (
                      <div className="rules-php-marks-row">
                        <label htmlFor="rules-lifeline-count" className="rules-php-marks-label">
                          Play count
                        </label>
                        <input
                          id="rules-lifeline-count"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          className="themeupdate-input rules-php-number"
                          value={endlessLifelineCount}
                          onChange={(e) =>
                            setEndlessLifelineCount(
                              normalizeMarksInput(e.target.value, MAX_LIFELINE_COUNT)
                            )
                          }
                          onBlur={() => {
                            const n = parseInt(endlessLifelineCount, 10);
                            if (
                              endlessLifelineCount === "" ||
                              Number.isNaN(n) ||
                              n < MIN_LIFELINE_COUNT
                            ) {
                              setEndlessLifelineCount(String(MIN_LIFELINE_COUNT));
                            } else {
                              setEndlessLifelineCount(
                                String(
                                  clampInt(
                                    n,
                                    MIN_LIFELINE_COUNT,
                                    MAX_LIFELINE_COUNT,
                                    MIN_LIFELINE_COUNT
                                  )
                                )
                              );
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="rules-php-marks-row rules-php-toggle-row">
                  <label htmlFor="rules-quiz-runner" className="rules-php-marks-label">
                    Quiz runner
                  </label>
                  <Switch
                    id="rules-quiz-runner"
                    checked={quizRunner}
                    onChange={setQuizRunner}
                    onColor={buttonColor}
                    offColor="#cbd5e1"
                    onHandleColor="#ffffff"
                    offHandleColor="#ffffff"
                    height={22}
                    width={48}
                    handleDiameter={18}
                    uncheckedIcon={false}
                    checkedIcon={false}
                  />
                </div>
              </div>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 20 }}>
                Page Content
              </h4>

              <div className="rules-tour-scoring" data-tour="tour-scoring">
                {quizRunner ? (
                <div className="rules-php-marks-row">
                  <label htmlFor="rules-points" className="rules-php-marks-label">
                    points per right answer
                  </label>
                  <input
                    id="rules-points"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="themeupdate-input rules-php-number"
                    value={points}
                    onChange={(e) => setPoints(normalizeMarksInput(e.target.value, MAX_SCORE_MARKS))}
                    onBlur={() => {
                      const n = parseInt(points, 10);
                      if (points === "" || Number.isNaN(n) || n < MIN_SCORE_MARKS) {
                        setPoints(String(MIN_SCORE_MARKS));
                      } else {
                        setPoints(String(clampInt(n, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS)));
                      }
                    }}
                  />
                </div>
                ) : null}

                <div className="rules-php-marks-row">
                  <label htmlFor="rules-obstacle-points" className="rules-php-marks-label">
                    Obstacle cross points
                  </label>
                  <input
                    id="rules-obstacle-points"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="themeupdate-input rules-php-number"
                    value={obstaclePoints}
                    onChange={(e) =>
                      setObstaclePoints(normalizeMarksInput(e.target.value, MAX_SCORE_MARKS))
                    }
                    onBlur={() => {
                      const n = parseInt(obstaclePoints, 10);
                      if (obstaclePoints === "" || Number.isNaN(n) || n < MIN_SCORE_MARKS) {
                        setObstaclePoints(String(MIN_SCORE_MARKS));
                      } else {
                        setObstaclePoints(
                          String(clampInt(n, MIN_SCORE_MARKS, MAX_SCORE_MARKS, MIN_SCORE_MARKS))
                        );
                      }
                    }}
                  />
                </div>

                {quizRunner ? (
                <div className="rules-php-marks-row">
                  <label htmlFor="rules-question-on-obstacle" className="rules-php-marks-label">
                    Question after obstacles crossed
                  </label>
                  <input
                    id="rules-question-on-obstacle"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="themeupdate-input rules-php-number"
                    value={questionOnObstacle}
                    onChange={(e) =>
                      setQuestionOnObstacle(normalizeMarksInput(e.target.value, MAX_SCORE_MARKS))
                    }
                    onBlur={() => {
                      const n = parseInt(questionOnObstacle, 10);
                      if (questionOnObstacle === "" || Number.isNaN(n) || n < MIN_SCORE_MARKS) {
                        setQuestionOnObstacle("2");
                      } else {
                        setQuestionOnObstacle(
                          String(clampInt(n, MIN_SCORE_MARKS, MAX_SCORE_MARKS, 2))
                        );
                      }
                    }}
                  />
                </div>
                ) : null}
              </div>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-title-font-px" className="rules-php-marks-label" style={{ display: 'none' }}>
                  Title font size (desktop, px)
                </label>
                <input style={{ display: 'none' }}
                  id="rules-title-font-px"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="themeupdate-input rules-php-number"
                  value={titleDesktopFontPx}
                  onChange={(e) =>
                    setTitleDesktopFontPx(normalizeTitleFontPxInput(e.target.value, MAX_TITLE_FONT_PX))
                  }
                  onBlur={() => {
                    const n = parseInt(titleDesktopFontPx, 10);
                    if (titleDesktopFontPx === "" || Number.isNaN(n) || n < MIN_TITLE_FONT_PX) {
                      setTitleDesktopFontPx(String(DEFAULT_TITLE_FONT_PX));
                    } else {
                      setTitleDesktopFontPx(
                        String(clampInt(n, MIN_TITLE_FONT_PX, MAX_TITLE_FONT_PX, DEFAULT_TITLE_FONT_PX))
                      );
                    }
                  }}
                />
              </div>

              <div className="rules-tour-branding" data-tour="tour-branding">
                <div className="rules-php-marks-row rules-php-color-row">
                  <label htmlFor="rules-title-color" className="rules-php-marks-label">
                    Text color
                  </label>
                  <input
                    id="rules-title-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={titleColor}
                    onChange={(e) => setTitleColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row" style={{ display: 'none' }}>
                  <label htmlFor="rules-game-text-color" className="rules-php-marks-label">
                    Game text colour
                  </label>
                  <input
                    id="rules-game-text-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={gameTextColor}
                    onChange={(e) => setGameTextColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row" style={{ display: 'none' }}>
                  <label htmlFor="rules-belt-color" className="rules-php-marks-label">
                    Title box colour
                  </label>
                  <input
                    id="rules-belt-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={beltColor}
                    onChange={(e) => setBeltColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row">
                  <label htmlFor="rules-btn-color" className="rules-php-marks-label">
                    Button gradient — color 1
                  </label>
                  <input
                    id="rules-btn-color"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-color-row">
                  <label htmlFor="rules-btn-color-2" className="rules-php-marks-label">
                    Button gradient — color 2
                  </label>
                  <input
                    id="rules-btn-color-2"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={buttonColor2}
                    onChange={(e) => setButtonColor2(e.target.value)}
                  />
                </div>

                <div
                  className="rules-php-gradient-preview"
                  style={{
                    background: `linear-gradient(135deg, ${buttonColor}, ${buttonColor2})`,
                  }}
                  aria-hidden
                />

                <div className="rules-php-marks-row rules-php-color-row">
                  <label htmlFor="rules-btn-text" className="rules-php-marks-label">
                    Button text color
                  </label>
                  <input
                    id="rules-btn-text"
                    type="color"
                    className="themeupdate-color-input rules-php-color-input"
                    value={buttonTextColor}
                    onChange={(e) => setButtonTextColor(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div
            className="themeupdate-card rules-php-card"
            data-tour="tour-begin-bg"
          >
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Begin backgrounds</h4>
              <p className="themeupdate-muted" style={{ marginTop: 0 }}>
                Upload desktop and mobile backgrounds shown on the begin screen.
              </p>

              <div className="rules-begin-upload-grid">
                {BEGIN_IMAGE_FIELDS.map((field) => (
                  <div className="rules-begin-upload-card" key={field.key}>
                    <label className="rules-begin-upload-label" htmlFor={`rules-${field.key}`}>
                      {field.label}
                    </label>
                    <button
                      type="button"
                      className="rules-begin-preview"
                      onClick={() => beginFileInputs.current[field.key]?.click()}
                      disabled={loading}
                    >
                      {beginPreviews[field.key] ? (
                        <img src={beginPreviews[field.key]} alt={`${field.label} preview`} />
                      ) : (
                        <span className="rules-begin-placeholder">
                          <i className="fa-solid fa-upload" aria-hidden />
                          Choose image
                        </span>
                      )}
                    </button>
                    <p className="themeupdate-muted rules-begin-hint">{field.hint}</p>
                    <input
                      id={`rules-${field.key}`}
                      ref={(el) => {
                        beginFileInputs.current[field.key] = el;
                      }}
                      type="file"
                      accept="image/jpeg,image/png"
                      className="rules-begin-file-input"
                      onChange={(e) => handleBeginFileChange(field, e)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder" data-tour="tour-actions">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={loading}>
          Save
        </button>
        <button type="button" className="save-and-continue rules-continue-outline" onClick={() => navigate("/admin/themeupdate")}>
          Continue to Categories
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </>
  );
};

export default Rules;
